import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiErrorMessage } from "../../../services/apiError";
import { newClientId } from "./clientId";
import { createSetQueue, queuedSessionIds } from "./setQueue";
import {
  MAX_SETS_PER_BATCH,
  RETRY_MS,
  failureKind,
  queuedSetText,
  refusalText,
  refusedIndex,
  throttleWaitMs,
  wireSet,
} from "./sessionSets";

const hasUser = (userId) => userId !== null && userId !== undefined && userId !== "";

/** createSetQueue's shape, held in memory: no signed-in id to key storage by. */
function memoryQueue() {
  let items = [];
  return {
    key: null,
    enqueue(item) {
      const at = items.findIndex((queued) => queued.client_id === item.client_id);
      items = at === -1 ? [...items, item] : items.map((queued, i) => (i === at ? item : queued));
      return false;
    },
    pending: () => items,
    ack(clientIds) {
      const done = new Set([].concat(clientIds ?? []));
      items = items.filter((queued) => !done.has(queued.client_id));
      return items;
    },
    clear() {
      items = [];
    },
    persisted: false,
  };
}

const isVisible = () =>
  typeof document === "undefined" || document.visibilityState !== "hidden";

/**
 * D14's set queue, live: every set is written to the person's queue
 * (lib/setQueue.js) the moment it's logged, then sent — the session's
 * queued sets in atomic batches of up to MAX_SETS_PER_BATCH (the API's
 * cap), one batch in flight at a time — and
 * removed only once the server has acknowledged it. A set that can't reach
 * mono stays queued and is sent again with the SAME client_id (so it's
 * never logged twice): on the next log, on mount, when the page or the
 * connection comes back, after RETRY_MS on its own, and on `retry()`.
 *
 * A set the server refuses (a 400 naming it — retrying can't help) is held
 * back with the server's words in `refused`, the rest of the batch goes
 * without it, and the person fixes it (`replace`) or drops it (`discard`).
 * A refused set was never stored (a retry of a stored set is answered,
 * never re-judged), so both are safe.
 *
 * `log(body, {label})` keeps the set's line beside it on this device (never
 * sent), so a refusal found later can say which set it was.
 *
 * `send(sets)` posts a batch (trainingApi.sets.log); `onSaved(result,
 * sent)` gets each acknowledged batch's TrainingSetBatchResult. `known`
 * (memoized: a Set or array of client_ids) is what the server is already
 * known to have — the session's own sets: a queued set among them was
 * stored and only its answer lost, so it's let go, not sent again.
 *
 * @returns {{
 *   pending: object[], sending: boolean,
 *   failure: {kind: string, message: string} | null,
 *   refused: Record<string, string>, persisted: boolean,
 *   log: (body: object, local?: {label?: string}) => object, retry: () => void,
 *   replace: (clientId: string, body: object, local?: {label?: string}) => void,
 *   discard: (clientId: string) => void,
 *   clear: () => void,
 * }}
 */
export default function useSetQueue({ userId, sessionId, send, onSaved, known }) {
  const queue = useMemo(
    () => (hasUser(userId) ? createSetQueue(userId, sessionId) : memoryQueue()),
    [userId, sessionId],
  );
  const [pending, setPending] = useState(() => queue.pending());
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState(null);
  const [refused, setRefused] = useState({});
  const [persisted, setPersisted] = useState(() => hasUser(userId));

  const queueRef = useRef(queue);
  queueRef.current = queue;
  const sendRef = useRef(send);
  sendRef.current = send;
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;
  const knownRef = useRef(known);
  knownRef.current = known;
  const refusedRef = useRef({});
  const sendingRef = useRef(false);
  const againRef = useRef(false);
  const aliveRef = useRef(true);
  const timerRef = useRef(null);
  const flushRef = useRef(null);

  // What's queued (and refused) now, on screen.
  const show = useCallback(() => {
    if (!aliveRef.current) return;
    setPending(queueRef.current.pending());
    setRefused({ ...refusedRef.current });
  }, []);

  const flush = useCallback(async () => {
    if (sendingRef.current) {
      againRef.current = true; // after the one in flight
      return;
    }
    const q = queueRef.current;
    const waiting = q.pending().filter((item) => !refusedRef.current[item.client_id]);
    if (!waiting.length) return;
    // The API takes at most a batch; the rest go right after this one.
    const items = waiting.slice(0, MAX_SETS_PER_BATCH);
    if (waiting.length > items.length) againRef.current = true;
    clearTimeout(timerRef.current);
    sendingRef.current = true;
    setSending(true);
    try {
      const response = await sendRef.current(items.map((item) => wireSet(item)));
      // Acknowledged: off the queue whether or not this page is still here.
      q.ack(items.map((item) => item.client_id));
      if (!aliveRef.current || q !== queueRef.current) return;
      setFailure(null);
      show();
      onSavedRef.current?.(response?.data ?? { sets: [], notices: [] }, items);
    } catch (err) {
      if (!aliveRef.current || q !== queueRef.current) return;
      const kind = failureKind(err);
      if (kind === "refused") {
        const index = refusedIndex(err, items.length);
        const message = refusalText(err);
        for (const item of index === null ? items : [items[index]]) {
          refusedRef.current[item.client_id] = message;
        }
        // The batch is all or nothing: the others weren't stored either.
        if (index !== null && items.length > 1) againRef.current = true;
        setFailure(null);
        show();
      } else {
        setFailure({ kind, message: apiErrorMessage(err, "No answer from mono.") });
        if (kind === "offline" || kind === "throttled") {
          timerRef.current = setTimeout(
            () => flushRef.current?.(),
            kind === "throttled" ? throttleWaitMs(err) : RETRY_MS,
          );
        }
      }
    } finally {
      sendingRef.current = false;
      if (aliveRef.current) setSending(false);
      if (againRef.current && aliveRef.current) {
        againRef.current = false;
        flushRef.current?.();
      }
    }
  }, [show]);
  flushRef.current = flush;

  // Queued sets the server already has: stored, only the answer was lost.
  const letGoKnown = useCallback(() => {
    const ids = new Set(knownRef.current ?? []);
    if (!ids.size) return;
    const q = queueRef.current;
    const stored = q.pending().filter((item) => ids.has(item.client_id));
    if (!stored.length) return;
    q.ack(stored.map((item) => item.client_id));
    show();
  }, [show]);

  // A new queue (another session or person): its own sets; send what waits.
  useEffect(() => {
    aliveRef.current = true;
    refusedRef.current = {};
    setRefused({});
    setFailure(null);
    letGoKnown();
    setPending(queue.pending());
    flushRef.current();
    return () => {
      aliveRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, [queue, letGoKnown]);

  // A newer copy of the session can show a set that was still waiting.
  useEffect(() => {
    letGoKnown();
  }, [known, letGoKnown]);

  // Back from a locked phone, another tab, or no signal: send what waits.
  useEffect(() => {
    const wake = () => {
      if (isVisible()) flushRef.current?.();
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("online", wake);
    return () => {
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("online", wake);
    };
  }, []);

  const log = useCallback((body, { label } = {}) => {
    const item = {
      ...body,
      client_id: newClientId(),
      performed_at: new Date().toISOString(),
      ...(label ? { label } : {}),
    };
    const q = queueRef.current;
    const stored = q.enqueue(item);
    setPersisted(stored);
    show();
    flushRef.current();
    return item;
  }, [show]);

  const retry = useCallback(() => {
    setFailure(null);
    flushRef.current();
  }, []);

  const replace = useCallback((clientId, body, { label } = {}) => {
    const q = queueRef.current;
    const queued = q.pending().find((item) => item.client_id === clientId);
    if (!queued) return;
    const line = label ?? queued.label;
    setPersisted(
      q.enqueue({
        ...body,
        client_id: clientId,
        performed_at: queued.performed_at,
        ...(line ? { label: line } : {}),
      }),
    );
    delete refusedRef.current[clientId];
    show();
    flushRef.current();
  }, [show]);

  const discard = useCallback((clientId) => {
    const left = queueRef.current.ack([clientId]);
    delete refusedRef.current[clientId];
    // The failure was about sets that waited: with none left, it's over.
    if (!left.length) setFailure(null);
    show();
  }, [show]);

  const clear = useCallback(() => {
    queueRef.current.clear();
    refusedRef.current = {};
    setFailure(null);
    show();
  }, [show]);

  return { pending, sending, failure, refused, persisted, log, retry, replace, discard, clear };
}

// Leftover batches mono refused without naming a set, this page load: not
// sent again until the next visit (or until the queue changes).
const refusedThisVisit = new Set();

/** Tests: a fresh "visit". */
export function forgetLeftoverRefusals() {
  refusedThisVisit.clear();
}

const setsWord = (n) => `${n} ${n === 1 ? "set" : "sets"}`;

// backend/training/services.py STALE_THIS_NOTICE — what log_sets says when
// the session the sets went to was idle and is closed now. For leftover sets
// that session is the LAST one, and "start a new session to keep going" is
// already what the person is doing (f9): said as walkthrough 10 says it.
const STALE_THIS = /^This session had been idle for over 6 hours/;
const LEFTOVERS_CLOSED =
  "Closed your previous session (no feedback) — it had been idle for over 6 hours. The sets still on this device were added to it.";
const asLeftovers = (notice) => (STALE_THIS.test(notice) ? LEFTOVERS_CLOSED : notice);

/**
 * Sets still queued for this person's OTHER sessions — a session finished
 * (or closed itself, D18) before its last sets got through. The API takes
 * sets into a finished session, so each is sent now (with the time it was
 * done), in batches of up to MAX_SETS_PER_BATCH.
 *
 * - A set mono refuses for good (a 400 naming it: a band renamed since, a
 *   set more than 7 days old) is dropped and reported; the rest of its
 *   batch goes again without it.
 * - A refusal that names no set keeps the batch, isn't tried again this
 *   visit, and is reported.
 * - A session that isn't there any more (404, discarded) can't take its
 *   sets: they're dropped and reported — unless the person asked to
 *   discard it (the queue is marked: the answer was lost to a reload), when
 *   they went with it as asked.
 * - No answer (offline, throttled, signed out) leaves them for next time.
 *
 * Every round sends one batch or drops one set, so it ends.
 *
 * @returns {Promise<{sent: number, notices: string[],
 *   dropped: {text: string, reason: string}[]}>} `notices` are the
 *   server's notices from the batches it took (e.g. "Closed your previous
 *   session…") and this function's own reports; `dropped` the sets mono
 *   won't ever take, each with mono's reason.
 */
export async function sendLeftovers(userId, currentSessionId, send) {
  const result = { sent: 0, notices: [], dropped: [] };
  if (!hasUser(userId)) return result;
  const note = (more) => {
    for (const notice of more ?? []) {
      if (notice && !result.notices.includes(notice)) result.notices.push(notice);
    }
  };
  for (const id of queuedSessionIds(userId)) {
    if (String(id) === String(currentSessionId)) continue;
    const q = createSetQueue(userId, id);
    // Every round acknowledges a batch or drops a set (an ack always lands:
    // storage.js keeps a refused write in memory), so this many rounds is
    // enough — and the cap holds even if that ever stops being true.
    const rounds = q.pending().length;
    for (let round = 0; round < rounds; round += 1) {
      const items = q.pending();
      if (!items.length) break;
      const batch = items.slice(0, MAX_SETS_PER_BATCH);
      const mark = `${userId}:${id}:${batch.map((item) => item.client_id).join(",")}`;
      if (refusedThisVisit.has(mark)) break;
      try {
        const response = await send(id, batch.map((item) => wireSet(item)));
        q.ack(batch.map((item) => item.client_id));
        result.sent += batch.length;
        note((response?.data?.notices ?? []).map(asLeftovers));
      } catch (err) {
        const kind = failureKind(err);
        if (kind === "gone") {
          // Gone as the person asked (a discard whose answer never reached
          // this device): the sets went with it, as its confirm said.
          if (!q.discarding) {
            const reason = apiErrorMessage(err, "That session isn't on mono any more.");
            for (const item of items) result.dropped.push({ text: queuedSetText(item), reason });
          }
          q.clear();
          break;
        }
        if (kind !== "refused") return result; // no answer: they wait for next time
        const index = refusedIndex(err, batch.length);
        if (index === null) {
          refusedThisVisit.add(mark);
          note([
            `${setsWord(batch.length)} from an earlier session couldn't be sent — mono said: ${refusalText(err)} They're kept on this device.`,
          ]);
          break;
        }
        const bad = batch[index];
        q.ack([bad.client_id]);
        result.dropped.push({ text: queuedSetText(bad), reason: refusalText(err) });
      }
    }
  }
  return result;
}
